import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';

function Bubble({ bubble }) {
  return (
    <motion.div
      initial={{ opacity: 0.4, scale: 1 }}
      animate={{
        opacity: [0.4, 0.48, 0.4],
        scale: [1, 1.1, 1],
      }}
      transition={{
        duration: bubble.duration,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: bubble.delay,
      }}
      style={{
        position: 'absolute',
        left: bubble.x,
        top: bubble.y,
        width: bubble.size,
        height: bubble.size,
        borderRadius: '50%',
        background:
          'radial-gradient(circle at 30% 30%, rgba(132, 204, 22, 0.9), rgba(21, 128, 61, 0.8))',
        boxShadow:
          '0 0 100px rgba(34, 197, 94, 0.5), 0 0 200px rgba(132, 204, 22, 0.4)',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
}

const pulseVariants = {
  initial: { scale: 0.5, opacity: 0.6 },
  animate: {
    scale: 3,
    opacity: 0,
    transition: { duration: 0.4, ease: 'easeOut' },
  },
};

function LandingPage({ onLetsGoClick }) {
  const [bubbles, setBubbles] = useState([]);
  const [hovered, setHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [shake, setShake] = useState(false);

  const controls = useAnimation();
  const wrapperControls = useAnimation();
  const isMounted = useRef(false);

  const relativeBubbles = useMemo(
    () => [
      { id: 1, sizeRatio: 0.3, xRatio: 0.05, yRatio: 0.05, duration: 8.0, delay: 0.0 },
      { id: 2, sizeRatio: 0.35, xRatio: 0.6, yRatio: 0.1, duration: 8.8, delay: 0.35 },
      { id: 3, sizeRatio: 0.25, xRatio: 0.1, yRatio: 0.7, duration: 7.4, delay: 0.7 },
      { id: 4, sizeRatio: 0.4, xRatio: 0.55, yRatio: 0.6, duration: 9.6, delay: 0.15 },
      { id: 5, sizeRatio: 0.3, xRatio: 0.3, yRatio: 0.4, duration: 8.4, delay: 0.9 },
    ],
    []
  );

  const [buttonSizes, setButtonSizes] = useState({
    rectWidth: 0,
    rectHeight: 0,
    circleSize: 0,
  });

  useEffect(() => {
    const recalculateLayout = () => {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;

      setBubbles(
        relativeBubbles.map((b) => ({
          ...b,
          size: screenWidth * b.sizeRatio,
          x: screenWidth * b.xRatio,
          y: screenHeight * b.yRatio,
        }))
      );

      setButtonSizes({
        rectWidth: Math.min(screenWidth * 0.4, 500),
        rectHeight: Math.min(screenWidth * 0.15, 250),
        circleSize: Math.min(screenWidth * 0.3, 300),
      });
    };

    recalculateLayout();

    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(recalculateLayout, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, [relativeBubbles]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (hovered && !isClicked) {
      controls.start({
        y: [0, -15, 0],
        transition: {
          repeat: Infinity,
          duration: 1.5,
          ease: 'easeInOut',
        },
      });
    } else if (!isClicked) {
      controls.stop();
      if (isMounted.current) {
        controls.start({
          scale: 1,
          opacity: 1,
          y: 0,
          transition: { duration: 0 },
        });
      }
    }
  }, [hovered, isClicked, controls]);

  const handleClick = async () => {
    if (isClicked || !isMounted.current) return;

    setIsClicked(true);
    setShake(true);
    controls.stop();

    await wrapperControls.start({
      x: [0, -10, 10, -10, 10, 0],
      y: [0, -5, 5, -5, 5, 0],
      rotate: [0, -2, 2, -2, 2, 0],
      transition: {
        duration: 0.4,
        ease: 'easeInOut',
      },
    });

    if (!isMounted.current) return;

    await controls.start({
      scale: [1, 1.8, 1.5, 1.9, 1.6, 2, 2.2],
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        times: [0, 0.2, 0.35, 0.5, 0.65, 0.8, 1],
        ease: 'easeOut',
      },
    });

    if (!isMounted.current) return;

    setShake(false);

    await controls.start({
      scale: 2.5,
      opacity: 0,
      transition: { duration: 0.15, ease: 'easeIn' },
    });

    if (!isMounted.current) return;

    await wrapperControls.start({
      y: '-100vh',
      opacity: 0.92,
      transition: {
        duration: 0.25,
        ease: [0.22, 1, 0.36, 1],
      },
    });

    if (!isMounted.current) return;

    if (onLetsGoClick) onLetsGoClick();
  };

  const showCircle = hovered || isClicked;
  const wrapperSize = showCircle ? buttonSizes.circleSize : buttonSizes.rectWidth;
  const wrapperHeight = showCircle ? buttonSizes.circleSize : buttonSizes.rectHeight;

  return (
    <motion.div
      className={`relative min-h-screen flex flex-col items-center justify-center font-montserrat overflow-hidden bg-white px-4 ${
        shake ? 'shake' : ''
      }`}
      animate={wrapperControls}
      initial={{ x: 0, y: 0, rotate: 0, opacity: 1 }}
      style={{ willChange: 'transform, opacity' }}
    >
      {bubbles.map((bubble) => (
        <Bubble key={bubble.id} bubble={bubble} />
      ))}

      <div className="relative z-10 max-w-screen-md mx-auto flex flex-col items-center">
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
          className="text-green-800 mb-8 font-light"
          style={{
            fontSize: '8vw',
            whiteSpace: 'nowrap',
            textAlign: 'center',
          }}
        >
          Hi! I&apos;m AJ Lange
        </motion.h1>

        <div
          className="relative"
          style={{
            width: wrapperSize,
            height: wrapperHeight,
            margin: 'auto',
            transition: 'width 0.4s ease, height 0.4s ease',
          }}
        >
          <AnimatePresence>
            {isClicked &&
              [0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  variants={pulseVariants}
                  initial="initial"
                  animate="animate"
                  exit={{ opacity: 0 }}
                  transition={{ delay: i * 0.15 }}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: wrapperSize,
                    height: wrapperHeight,
                    borderRadius: '50%',
                    border: '3px solid #166534',
                    pointerEvents: 'none',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1,
                  }}
                />
              ))}
          </AnimatePresence>

          <motion.button
            onClick={handleClick}
            onMouseEnter={() => !isClicked && setHovered(true)}
            onMouseLeave={() => !isClicked && setHovered(false)}
            animate={controls}
            initial={{ scale: 1, opacity: 1, y: 0 }}
            style={{
              width: '100%',
              height: '100%',
              borderRadius: showCircle ? '50%' : '0.75rem',
              backgroundColor: hovered ? '#166534' : 'transparent',
              color: hovered ? 'white' : '#166534',
              borderColor: '#166534',
              boxShadow: hovered
                ? '0 8px 24px rgba(22, 101, 52, 0.6)'
                : '0 2px 8px rgba(34, 197, 94, 0.3)',
              borderWidth: 2,
              borderStyle: 'solid',
              cursor: 'pointer',
              fontWeight: '300',
              fontSize: '5vw',
              userSelect: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 2,
              transition:
                'background-color 0.4s ease, color 0.4s ease, box-shadow 0.4s ease, border-radius 0.4s ease',
            }}
            className="select-none"
          >
            {!hovered && !isClicked && (
              <span style={{ pointerEvents: 'none' }} className="text-green-900">
                Wanna see my Page?
              </span>
            )}

            {hovered && !isClicked && (
              <motion.div
                key="circle-content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col items-center justify-center pointer-events-none"
              >
                <span style={{ fontWeight: 600, fontSize: '1.25rem' }}>
                  Let&apos;s go!
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-10 h-10 mt-2"
                  viewBox="0 0 24 24"
                  style={{ pointerEvents: 'none' }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </motion.div>
            )}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
}

export default LandingPage;